import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";

import { JSX, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/buttons/Button.tsx";
import CreditCard from "@/components/cards/CreditCard/CreditCard.tsx";
import { IconCaretRight, IconX } from "@/components/icons";
import AddCardModal from "@/components/staff/payment/modal/AddCardModal/AddCardModal.tsx";

import { useGetCreditCards } from "@/hooks/api/user.hooks";
import { cn } from "@/utils/cn.ts";

const CardsDrawer = (): JSX.Element => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const { data: creditCards } = useGetCreditCards();

  return (
    <>
      <button
        aria-label={t("Payment.top-up")}
        onClick={() => setIsOpen(true)}
        className={
          "group flex items-center text-base/5 text-grey-1 transition-colors hover:text-on-primary max-md:text-xs/4"
        }
      >
        {t("General.all")}
        <IconCaretRight
          className={"size-4 stroke-grey-1 transition-colors group-hover:stroke-on-primary max-md:size-3.5"}
        />
      </button>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        transition
        className={
          "fixed inset-0 z-50 flex w-screen items-center justify-end overflow-hidden bg-[#000000CC] transition data-[closed]:opacity-0 max-sm:justify-center max-sm:px-4"
        }
      >
        <DialogPanel
          transition
          className={cn(
            "h-dvh w-full max-w-[27.125rem] overflow-y-auto rounded-bl-2xl rounded-tl-2xl bg-surface-container p-8 text-on-primary transition  max-sm:data-[closed]:scale-95 sm:data-[closed]:translate-x-1/2",
            "max-sm:h-fit max-sm:max-h-dvh max-sm:max-w-none max-sm:rounded-[1.5rem] max-sm:bg-surface-bright max-sm:px-4 max-sm:py-6",
          )}
        >
          <div className={"item-center flex justify-between"}>
            <DialogTitle className={"text-2xl font-semibold"}>{t("Payment.card", { count: 2 })}</DialogTitle>
            <button
              type={"button"}
              aria-label={t("Actions.close")}
              tabIndex={-1}
              onClick={() => setIsOpen(false)}
              className={"rounded-full bg-surface-container-high p-2 sm:hidden"}
            >
              <IconX className={"size-6 stroke-on-primary"} />
            </button>
          </div>
          <hr className={"mt-6 h-px w-full border-surface-container max-sm:hidden"} />
          <div className={"mt-3 space-y-4 max-sm:mt-6"}>
            {creditCards.map((item) => (
              <CreditCard creditCard={item} />
            ))}
          </div>
          {creditCards.length == 0 ? <p className="my-5 text-center text-xl">{t("cards.empty")}</p> : <></>}
          <Suspense
            fallback={
              <Button size={"medium"} aria-label={t("Payment.add-card")} className={"mt-5"}>
                {t("Payment.add-card")}
              </Button>
            }
          >
            <AddCardModal className="mt-5" />
          </Suspense>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default CardsDrawer;
